"use client";

import Image from "next/image";
import Link from "next/link";
import { Linkedin, MapPin } from "lucide-react";

import { Container } from "@/components/primitives/Container";
import { FooterNewsletter } from "@/components/layout/FooterNewsletter";
import { ContactCTA } from "@/components/sections/ContactCTA";
import CompanyIdentityBlock from "@/components/sections/CompanyIdentityBlock";
import { getSiteContent } from "@/content/site";
import { brandLogo } from "@/lib/assets";
import { openCookiePreferences } from "@/lib/cookie-consent";
import { useLocale } from "@/lib/i18n/provider";
import { newsNavLabel } from "@/lib/news/news-locale-redirect.mjs";
import { localizePath } from "@/lib/i18n/routing";

export default function Footer() {
  const { locale } = useLocale();
  const { footer, nav } = getSiteContent(locale);
  const year = new Date().getFullYear();

  return (
    <footer className="relative border-t border-[var(--color-surface-border)] bg-[var(--color-bg-sunken)]">
      {/* ── Newsletter banner ───────────────────────────────────────── */}
      <div className="border-b border-[var(--color-surface-border)]">
        <Container className="py-10">
          <div className="flex flex-col gap-6 sm:flex-row sm:items-center sm:justify-between">
            <div className="max-w-md">
              <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-[var(--color-text-muted)]">
                {footer.newsletter.title}
              </p>
              <p className="mt-2 text-[15px] leading-relaxed text-[var(--color-text-secondary)]">
                {footer.newsletter.body}
              </p>
            </div>
            <div className="w-full max-w-sm shrink-0">
              <FooterNewsletter compact />
            </div>
          </div>
        </Container>
      </div>

      {/* ── Main grid ────────────────────────────────────────────────── */}
      <Container className="py-14 md:py-16">
        <div className="grid grid-cols-2 gap-x-8 gap-y-12 md:grid-cols-[1.8fr_1fr_1fr_1fr]">

          {/* Brand column */}
          <div className="col-span-2 md:col-span-1">
            <Link href={localizePath("/", locale)} aria-label={nav.brandLabel} className="inline-flex rounded-xl">
              <Image
                src={brandLogo.layout02Light}
                alt={nav.brandLabel}
                width={96}
                height={72}
                className="h-12 w-auto [filter:var(--logo-filter-negative)]"
              />
            </Link>

            <p className="mt-5 max-w-[34ch] text-sm leading-relaxed text-[var(--color-text-secondary)]">
              {footer.positioning}
            </p>

            {/* Social */}
            <div className="mt-7">
              <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-[var(--color-text-muted)]">
                {footer.social.title}
              </p>
              <ul className="mt-3 flex items-center gap-3">
                {footer.social.links.map((link) => (
                  <li key={link.label}>
                    <a
                      href={link.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label={link.label}
                      className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-[var(--color-surface-border)] text-[var(--color-text-secondary)] transition-colors hover:border-[var(--color-accent-base)] hover:text-[var(--color-accent-base)]"
                    >
                      <Linkedin size={15} aria-hidden="true" />
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* Nav columns */}
          {footer.columns.map((col) => (
            <div key={col.title}>
              <h3 className="text-[11px] font-semibold uppercase tracking-[0.18em] text-[var(--color-text-muted)]">
                {col.title}
              </h3>
              <ul className="mt-4 flex flex-col gap-2.5">
                {col.links.map((link) => (
                  <li key={link.label}>
                    <Link
                      href={link.href}
                      className="text-sm text-[var(--color-text-secondary)] transition-colors hover:text-[var(--color-text-primary)]"
                    >
                      {link.label}
                    </Link>
                  </li>
                ))}
                {col.links.some((link) => link.href.includes("/about")) ? (
                  <li>
                    <Link
                      href={localizePath("/news", locale)}
                      className="text-sm text-[var(--color-text-secondary)] transition-colors hover:text-[var(--color-text-primary)]"
                    >
                      {newsNavLabel(locale)}
                    </Link>
                  </li>
                ) : null}
              </ul>
            </div>
          ))}

          {/* Contact column */}
          <div>
            <h3 className="text-[11px] font-semibold uppercase tracking-[0.18em] text-[var(--color-text-muted)]">
              {footer.contact.title}
            </h3>
            <ul className="mt-4 flex flex-col gap-2.5">
              <li>
                <ContactCTA label={footer.contact.triggerLabel} mode="text" />
              </li>
              <li>
                <a
                  href={footer.contact.emailHref}
                  className="text-sm text-[var(--color-text-secondary)] transition-colors hover:text-[var(--color-text-primary)]"
                >
                  {footer.contact.emailLabel}
                </a>
              </li>
            </ul>
          </div>
        </div>
      </Container>

      {/* ── Bottom bar ───────────────────────────────────────────────── */}
      <div className="border-t border-[var(--color-surface-border)]">
        <Container className="flex flex-col gap-4 py-5 text-xs text-[var(--color-text-muted)] sm:flex-row sm:items-center sm:justify-between">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between sm:gap-4">
            <p>
              &copy; {year} {footer.copyright.brandName}. {footer.copyright.rightsReserved}
            </p>
            <span
              className="hidden h-3 w-px bg-[var(--color-surface-border)] sm:block"
              aria-hidden="true"
            />
            <span className="flex items-center gap-1.5">
              <MapPin size={10} aria-hidden="true" />
              {footer.headquarters.name}, {footer.headquarters.lines[2]}
            </span>
            <CompanyIdentityBlock compact />
          </div>
          <ul className="flex flex-wrap items-center gap-x-4 gap-y-2">
            <li>
              <Link
                href={localizePath("/news", locale)}
                className="transition-colors hover:text-[var(--color-text-primary)]"
              >
                {newsNavLabel(locale)}
              </Link>
            </li>
            {footer.legal.map((link) => (
              <li key={link.label}>
                {link.consentPreferences ? (
                  <button
                    type="button"
                    onClick={() => openCookiePreferences()}
                    className="transition-colors hover:text-[var(--color-text-primary)]"
                  >
                    {link.label}
                  </button>
                ) : (
                  <a
                    href={localizePath(link.href, locale)}
                    className="transition-colors hover:text-[var(--color-text-primary)]"
                  >
                    {link.label}
                  </a>
                )}
              </li>
            ))}
          </ul>
        </Container>
      </div>
    </footer>
  );
}

